import React, { useState, useEffect } from "react";
import "./patientAddress.css";
import ToastPopup from "../../widgets/Toast/toastPopup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../widgets";
import { resetRejectHnReq } from "../../modules/patientProfile/patientProfileSlice";
import useValidation from "../../utils/validators";
import phoneNumberLenghth from "../../utils/phoneNumberLength";
import { useLocation } from "react-router-dom";

const PatientAddress = ({ userId, setSelectedView, setPage, userAddress, setRequestData, hasHn, occupiedTags }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { rejectHnReqErr, rejectHnReq } = useSelector((state) => state.patientProfile);
  const verifyType = useSelector((state) => state.verifyType.type);
  const { validatePersonalInfo } = useValidation();
  const [showReject, setShowReject] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const options = [
    { value: "home", label: t("home") },
    { value: "relative", label: t("relative") },
    { value: "other", label: t("other") },
  ];

  const filteredTagOptions = options.filter((option) => !occupiedTags.map((tag) => tag.toLowerCase()).includes(option.value.toLowerCase()));

  useEffect(() => {
    if (userAddress === null) {
      setRequestData((prevProfile) => ({
        ...prevProfile,
        userAddress: {
          ...prevProfile.userAddress,
          saveAs: filteredTagOptions[0].value.toLowerCase() === "other" ? "" : filteredTagOptions[0].value,
        },
      }));
    }

    if (!userAddress?.saveAs || !["home", "relative"].includes(userAddress.saveAs.toLowerCase())) {
      setShowInput(true);
    } else {
      setShowInput(false);
    }
  }, [userAddress]);

  useEffect(() => {
    if (rejectHnReqErr) {
      ToastPopup.error(rejectHnReqErr);
    }
    if (rejectHnReq && rejectHnReq.data.rejected) {
      ToastPopup.success(t("reqRejected"));
      setSelectedView("patientProfileList");
    }
    dispatch(resetRejectHnReq());
  }, [rejectHnReqErr, rejectHnReq]);

  const handleVerify = () => {
    console.log(userAddress);
    const isPersonalInfoValid = validatePersonalInfo(userAddress);
    if (isPersonalInfoValid) {
      if (verifyType === "withHN" && occupiedTags.map((tag) => tag.toLowerCase()).includes(userAddress?.saveAs.toLowerCase())) {
        ToastPopup.error(t("addTagOccupied"));
        return;
      }
      setPage(3);
    }
  };
  const toggleReject = (value) => {
    setShowReject(value);
  };
  const handleReject = () => toggleReject(true);
  const handleCancelReject = () => toggleReject(false);
  const handleConfirmReject = () => toggleReject(false);

  const getMaxLengthByCountryCode = (countryCode) => {
    const maxLength = Object.entries(phoneNumberLenghth).find(([length, codes]) => codes.includes(countryCode))?.[0];
    return maxLength;
  };

  const officeLength = getMaxLengthByCountryCode(userAddress?.officeCountryCode || "84");
  const phoneLength = getMaxLengthByCountryCode(userAddress?.personalCountryCode || "84");

  const handleInputChange = (e) => {
    const { name, value, selectionStart } = e.target;
    let newValue = value.slice(0, 100);

    const removeNonNumeric = (str) => str.replace(/\D/g, "");
    const removeLeadingSpaces = (str) => str.trimStart();

    switch (name) {
      case "personalPhoneNumber":
        newValue = removeNonNumeric(newValue);
        if (newValue.length > phoneLength) {
          newValue = newValue.slice(0, phoneLength);
        }
        break;
      case "officePhoneNumber":
        newValue = removeNonNumeric(newValue);
        if (newValue.length > officeLength) {
          newValue = newValue.slice(0, officeLength);
        }
        break;
      case "postalCode":
        newValue = removeNonNumeric(newValue);
        if (newValue.length > 10) {
          newValue = newValue.slice(0, 10);
        }
        break;
      default:
        newValue = removeLeadingSpaces(newValue);
        break;
    }

    setRequestData((prevProfile) => {
      const updatedProfile = {
        ...prevProfile,
        userAddress: {
          ...prevProfile.userAddress,
          [name]: newValue,
        },
      };
      return updatedProfile;
    });

    requestAnimationFrame(() => {
      const inputElement = e.target;
      inputElement.setSelectionRange(selectionStart, selectionStart);
    });
  };

  const handleCountryCodeChange = (field) => (newCountryCode) => {
    setRequestData((prevState) => ({
      ...prevState,
      userAddress: {
        ...(prevState.userAddress || {}),
        [field]: newCountryCode,
      },
    }));
  };

  const getPlaceholder = (field) => t("enter") + t(field);
  return (
    <div className="pav-container">
      <div className="pav-add-item verify">
        <div className="pav-add-header">
          <section>
            <p>{t("currentAdd")}</p>
          </section>
        </div>
        <div className="pav-add-details address">
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("phoneNumber")}
              <span className="asterik">*</span>
            </p>

            <div className="phone-input-group">
              <Widgets.CountryCodeSelect selectedValue={userAddress?.personalCountryCode || "84"} onChange={handleCountryCodeChange("personalCountryCode")} isDisabled={false} />

              <div className="line-bar"></div>

              <input
                type="text"
                name="personalPhoneNumber"
                className="pdl-number phone"
                value={userAddress?.personalPhoneNumber}
                onChange={handleInputChange}
                placeholder={getPlaceholder("phoneNumber")}
              />
            </div>
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">{t("officeNumber")}</p>
            <div className="phone-input-group">
              <Widgets.CountryCodeSelect selectedValue={userAddress?.officeCountryCode || "84"} onChange={handleCountryCodeChange("officeCountryCode")} isDisabled={false} />
              <div className="line-bar"></div>

              <input
                type="text"
                name="officePhoneNumber"
                className="pdl-number phone"
                value={userAddress?.officePhoneNumber}
                onChange={handleInputChange}
                placeholder={getPlaceholder("officeNumber")}
              />
            </div>
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("street")}
              <span className="asterik">*</span>
            </p>
            <input type="text" name="street" className="pdl-number" value={userAddress?.street} onChange={handleInputChange} placeholder={getPlaceholder("street")} />
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("ward")}
              <span className="asterik">*</span>
            </p>
            <input type="text" name="ward" className="pdl-number" value={userAddress?.ward} onChange={handleInputChange} placeholder={getPlaceholder("ward")} />
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("district")}
            </p>
            <input type="text" name="district" className="pdl-number" value={userAddress?.district} onChange={handleInputChange} placeholder={getPlaceholder("district")} />
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("city")}
              <span className="asterik">*</span>
            </p>
            <input type="text" name="city" className="pdl-number" value={userAddress?.city} onChange={handleInputChange} placeholder={getPlaceholder("city")} />
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("province")}
              <span className="asterik">*</span>
            </p>
            <input type="text" name="province" className="pdl-number" value={userAddress?.province} onChange={handleInputChange} placeholder={getPlaceholder("province")} />
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">{t("postalCode")}</p>
            <input type="text" name="postalCode" className="pdl-number" value={userAddress?.postalCode} onChange={handleInputChange} placeholder={getPlaceholder("postalCode")} />
          </div>
          <div className="pdl-hn">
            <p className="pdl-hn-title">
              {t("addTag")}
              <span className="asterik">*</span>
            </p>

            <p className="">
              <Widgets.NewSelectDropdown
                options={filteredTagOptions}
                selectedValue={
                  userAddress?.saveAs && ["home", "relative"].includes(userAddress.saveAs.toLowerCase())
                    ? {
                        value: userAddress.saveAs,
                        label: t(userAddress.saveAs),
                      }
                    : { value: "other", label: t("other") }
                }
                onChange={(selectedValue) => {
                  if (selectedValue.value === "other") {
                    setShowInput(true);
                    setRequestData((prevProfile) => ({
                      ...prevProfile,
                      userAddress: {
                        ...prevProfile.userAddress,
                        saveAs: "",
                      },
                    }));
                  } else {
                    setShowInput(false);
                    setRequestData((prevProfile) => ({
                      ...prevProfile,
                      userAddress: {
                        ...prevProfile.userAddress,
                        saveAs: selectedValue.value,
                      },
                    }));
                  }
                }}
                placeholder={t("selectSaveAs")}
                big
                disabled={userAddress?._id}
              />
              {showInput && (
                <input
                  type="text"
                  placeholder={t("entAddTag")}
                  value={userAddress?.saveAs}
                  onChange={(e) =>
                    setRequestData((prevProfile) => ({
                      ...prevProfile,
                      userAddress: {
                        ...prevProfile.userAddress,
                        saveAs: e.target.value,
                      },
                    }))
                  }
                  className="pdl-number"
                  disabled={userAddress?._id}
                />
              )}
            </p>
          </div>
        </div>
        <div className="save-speciality info">
          <>
            {hasHn && (
              <div className="speciality-cancel reject" onClick={handleReject}>
                {t("reject")}
              </div>
            )}
            <div className="speciality-save" onClick={() => handleVerify()}>
              {t("next")}
            </div>
          </>
        </div>
      </div>
      <Widgets.Reject showModal={showReject} handleCancel={handleCancelReject} handleConfirm={handleConfirmReject} reason="rejCurrentAdd" userId={userId} />
    </div>
  );
};

export default PatientAddress;
